import { useState, useEffect, useCallback } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

// ─────────────────────────────────────────────────────────────────────────────
// INTEGRACIONES — claves de la API pública (sólo superadmin)
//
// Una integración = una empresa + un integrador (por ejemplo, Laugero + Track),
// con los recursos que puede leer. Cada integración tiene una o dos claves:
// dos sólo durante el solapamiento de una rotación.
//
// Todo pasa por Cloud Functions: las colecciones api_* están cerradas en las
// reglas y el panel nunca ve el hash. La clave completa se muestra UNA vez,
// al crearla o rotarla.
//
// El ambiente no se elige: sale de la empresa. Una empresa marcada como de
// prueba (tenants.es_prueba) sólo recibe claves de prueba, y las demás, de
// producción. Así una clave "de prueba" nunca puede leer datos reales.
// ─────────────────────────────────────────────────────────────────────────────

const listarFn = httpsCallable(functions, "apiListarIntegraciones");
const crearFn = httpsCallable(functions, "apiCrearIntegracion");
const rotarFn = httpsCallable(functions, "apiRotarClave");
const revocarFn = httpsCallable(functions, "apiRevocarClave");
const actualizarFn = httpsCallable(functions, "apiActualizarIntegracion");
const resincronizarFn = httpsCallable(functions, "apiResincronizarIntegracion");

const DIA = 24 * 60 * 60 * 1000;

const C = {
  ink: "#1e293b", ink2: "#475569", ink3: "#64748b", line: "#e2e8f0",
  bg: "#f8fafc", ok: "#16A34A", warn: "#D97706", bad: "#DC2626",
};

function fecha(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
}
function fechaHora(iso) {
  if (!iso) return "Nunca";
  return new Date(iso).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}
function diasPara(iso) {
  return iso ? Math.ceil((new Date(iso).getTime() - Date.now()) / DIA) : 0;
}

function Chip({ color, children }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999,
      color, background: color + "14", border: `0.5px solid ${color}55`, whiteSpace: "nowrap",
    }}>
      {children}
    </span>
  );
}

const ESTADO_CLAVE = {
  activa: { txt: "Activa", color: C.ok },
  vencida: { txt: "Vencida", color: C.ink3 },
  revocada: { txt: "Revocada", color: C.bad },
};

// ── Modal: la clave se ve una sola vez ──────────────────────────────────────
function ClaveNueva({ datos, onCerrar }) {
  const [copiada, setCopiada] = useState(false);
  const [guardada, setGuardada] = useState(false);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(datos.clave);
      setCopiada(true);
    } catch {
      const el = document.getElementById("clave-api-texto");
      if (el) { el.focus(); el.select(); }
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div role="dialog" aria-modal="true" style={{ background: "white", borderRadius: 12, padding: 24, maxWidth: 560, width: "100%", boxShadow: "0 12px 40px rgba(0,0,0,0.25)" }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 17, color: C.ink }}>Clave de {datos.integrador}</h3>
        <p style={{ margin: "0 0 16px", fontSize: 13, color: C.ink3 }}>
          {datos.empresa} · vence el {fecha(datos.vence_at)}
        </p>
        <div style={{ background: "#FEF3C7", border: "0.5px solid #FCD34D", color: "#92400E", fontSize: 13, borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
          Esta es la única vez que se muestra. ObraCore no la guarda: si se pierde, hay que generar otra.
          Entregala por un canal seguro, nunca en un mail con copia ni en un chat grupal.
        </div>
        <textarea
          id="clave-api-texto"
          readOnly
          value={datos.clave}
          rows={2}
          onFocus={(e) => e.target.select()}
          style={{ width: "100%", boxSizing: "border-box", fontFamily: "'DM Mono', ui-monospace, Menlo, monospace", fontSize: 13, padding: 10, borderRadius: 8, border: `1px solid ${C.line}`, background: C.bg, resize: "none", wordBreak: "break-all" }}
        />
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between", marginTop: 12 }}>
          <button type="button" onClick={copiar} style={estilos.btn}>
            {copiada ? "Copiada" : "Copiar clave"}
          </button>
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, color: C.ink2 }}>
            <input id="clave-api-guardada" type="checkbox" checked={guardada} onChange={(e) => setGuardada(e.target.checked)} />
            Ya la guardé
          </label>
          <button type="button" disabled={!guardada} onClick={onCerrar} style={{ ...estilos.btnPrimario, opacity: guardada ? 1 : 0.4, cursor: guardada ? "pointer" : "not-allowed" }}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Formulario de alta ──────────────────────────────────────────────────────
function NuevaIntegracion({ tenants, permisosDisponibles, onCreada, onCancelar, avisar }) {
  const [tenantId, setTenantId] = useState("");
  const [nombre, setNombre] = useState("");
  const [permisos, setPermisos] = useState(Object.keys(permisosDisponibles).slice(0, 1));
  const [contacto, setContacto] = useState("");
  const [guardando, setGuardando] = useState(false);

  const tenant = tenants.find((t) => t.id === tenantId);
  const ambiente = tenant?.es_prueba ? "prueba" : "produccion";

  async function crear(e) {
    e.preventDefault();
    if (!tenantId || nombre.trim().length < 2 || !permisos.length) {
      avisar("Completá la empresa, el integrador y al menos un permiso.", "error");
      return;
    }
    setGuardando(true);
    try {
      const { data } = await crearFn({ tenant_id: tenantId, nombre: nombre.trim(), ambiente, permisos, contacto_email: contacto.trim() });
      onCreada({ ...data.clave, integrador: nombre.trim(), empresa: tenant?.name || tenantId });
    } catch (err) {
      avisar(err.message || "No se pudo crear la integración.", "error");
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form onSubmit={crear} style={estilos.tarjeta}>
      <h3 style={{ margin: "0 0 16px", fontSize: 16, color: C.ink }}>Nueva integración</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        <label style={estilos.campo}>
          <span style={estilos.etiqueta}>Empresa</span>
          <select id="int-empresa" value={tenantId} onChange={(e) => setTenantId(e.target.value)} style={estilos.input}>
            <option value="">Elegí una empresa…</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name || t.id}{t.es_prueba ? " (prueba)" : ""}</option>
            ))}
          </select>
        </label>
        <label style={estilos.campo}>
          <span style={estilos.etiqueta}>Integrador</span>
          <input id="int-nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Track" maxLength={60} style={estilos.input} />
        </label>
        <label style={estilos.campo}>
          <span style={estilos.etiqueta}>Contacto técnico (mail)</span>
          <input id="int-contacto" type="email" value={contacto} onChange={(e) => setContacto(e.target.value)} placeholder="sistemas@integrador.com" style={estilos.input} />
        </label>
      </div>

      <div style={{ marginTop: 14 }}>
        <span style={estilos.etiqueta}>Puede leer</span>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 6 }}>
          {Object.entries(permisosDisponibles).map(([p, txt]) => (
            <label key={p} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, color: C.ink2 }}>
              <input
                id={`int-permiso-${p}`}
                type="checkbox"
                checked={permisos.includes(p)}
                onChange={(e) => setPermisos(e.target.checked ? [...permisos, p] : permisos.filter((x) => x !== p))}
              />
              {txt}
            </label>
          ))}
        </div>
      </div>

      {tenant && (
        <p style={{ fontSize: 12.5, color: C.ink3, margin: "14px 0 0" }}>
          {ambiente === "prueba"
            ? "Empresa de prueba: la clave va a empezar con oc_test_ y sólo ve datos ficticios."
            : "Empresa real: la clave va a empezar con oc_live_. Antes de entregarla, el acuerdo de tratamiento de datos tiene que estar firmado."}
        </p>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16, flexWrap: "wrap" }}>
        <button type="button" onClick={onCancelar} style={estilos.btn}>Cancelar</button>
        <button type="submit" disabled={guardando} style={estilos.btnPrimario}>
          {guardando ? "Creando…" : "Crear y generar clave"}
        </button>
      </div>
    </form>
  );
}

// ── Una integración ─────────────────────────────────────────────────────────
function Integracion({ it, empresa, permisosDisponibles, onClaveNueva, onCambio, avisar }) {
  const [revocando, setRevocando] = useState(null); // id de clave
  const [pausando, setPausando] = useState(false);
  const [editando, setEditando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [permisos, setPermisos] = useState(it.permisos);
  const [contacto, setContacto] = useState(it.contacto_email || "");
  const [ocupado, setOcupado] = useState(false);

  const activas = it.claves.filter((c) => c.estado === "activa");
  const visibles = it.claves.filter((c) => c.estado === "activa" || diasPara(c.revocada_at || c.vence_at) > -90);

  function cerrarTodo() {
    setRevocando(null); setPausando(false); setEditando(false); setMotivo("");
  }

  async function correr(fn, ok) {
    setOcupado(true);
    try {
      const r = await fn();
      cerrarTodo();
      if (ok) avisar(ok, "ok");
      await onCambio();
      return r;
    } catch (err) {
      avisar(err.message || "No se pudo completar la operación.", "error");
      return null;
    } finally {
      setOcupado(false);
    }
  }

  async function volverAPublicar() {
    const r = await correr(() => resincronizarFn({ integracion_id: it.id }));
    if (!r) return;
    const cambios = Object.values(r.data.carga_inicial || {}).reduce((n, c) => n + (c?.cambios || 0), 0);
    const error = Object.values(r.data.carga_inicial || {}).find((c) => c?.error);
    avisar(error ? error.error : cambios ? `Publicadas ${cambios} novedad(es)` : "Ya estaba al día", error ? "error" : "ok");
  }

  async function rotar() {
    const r = await correr(() => rotarFn({ integracion_id: it.id }));
    if (r) onClaveNueva({ ...r.data.clave, integrador: it.nombre, empresa });
  }

  const permisosCambian = JSON.stringify([...permisos].sort()) !== JSON.stringify([...it.permisos].sort());

  return (
    <div style={estilos.tarjeta}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <strong style={{ fontSize: 16, color: C.ink }}>{it.nombre}</strong>
            <span style={{ fontSize: 14, color: C.ink3 }}>· {empresa}</span>
            <Chip color={it.ambiente === "prueba" ? C.warn : "#2563EB"}>{it.ambiente === "prueba" ? "Prueba" : "Producción"}</Chip>
            {it.estado === "pausada" && <Chip color={C.bad}>Pausada</Chip>}
          </div>
          <div style={{ fontSize: 12.5, color: C.ink3, marginTop: 4 }}>
            Lee: {it.permisos.map((p) => permisosDisponibles[p] || p).join(", ")}
            {it.contacto_email && <> · Contacto: {it.contacto_email}</>}
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          <button type="button" style={estilos.btnChico} disabled={ocupado || activas.length >= 2 || it.estado !== "activa"} onClick={rotar}
            title={activas.length >= 2 ? "Ya hay dos claves activas: revocá la vieja primero" : "Genera una clave nueva; la actual sigue 14 días"}>
            Rotar clave
          </button>
          <button type="button" style={estilos.btnChico} disabled={ocupado} onClick={volverAPublicar}
            title="Recalcula los datos de la empresa y publica sólo lo que cambió">
            Volver a publicar
          </button>
          <button type="button" style={estilos.btnChico} disabled={ocupado} onClick={() => { cerrarTodo(); setEditando(true); setPermisos(it.permisos); setContacto(it.contacto_email || ""); }}>
            Editar
          </button>
          {it.estado === "activa" ? (
            <button type="button" style={{ ...estilos.btnChico, color: C.bad }} disabled={ocupado} onClick={() => { cerrarTodo(); setPausando(true); }}>
              Pausar
            </button>
          ) : (
            <button type="button" style={estilos.btnChico} disabled={ocupado}
              onClick={() => correr(() => actualizarFn({ integracion_id: it.id, estado: "activa" }), "Integración reactivada")}>
              Reactivar
            </button>
          )}
        </div>
      </div>

      {pausando && (
        <Confirmacion
          texto="Pausar corta el acceso de todas las claves de esta integración hasta que la reactives."
          motivo={motivo} setMotivo={setMotivo} ocupado={ocupado} etiqueta="Pausar" peligro
          onCancelar={cerrarTodo}
          onConfirmar={() => correr(() => actualizarFn({ integracion_id: it.id, estado: "pausada", motivo }), "Integración pausada")}
        />
      )}

      {editando && (
        <div style={estilos.recuadro}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {Object.entries(permisosDisponibles).map(([p, txt]) => (
              <label key={p} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, color: C.ink2 }}>
                <input id={`ed-${it.id}-${p}`} type="checkbox" checked={permisos.includes(p)}
                  onChange={(e) => setPermisos(e.target.checked ? [...permisos, p] : permisos.filter((x) => x !== p))} />
                {txt}
              </label>
            ))}
          </div>
          <label style={{ ...estilos.campo, marginTop: 10 }}>
            <span style={estilos.etiqueta}>Contacto técnico (mail)</span>
            <input id={`ed-${it.id}-contacto`} type="email" value={contacto} onChange={(e) => setContacto(e.target.value)} style={estilos.input} />
          </label>
          {permisosCambian && (
            <label style={{ ...estilos.campo, marginTop: 10 }}>
              <span style={estilos.etiqueta}>Motivo del cambio de permisos</span>
              <input id={`ed-${it.id}-motivo`} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Acordado con el integrador el…" style={estilos.input} />
            </label>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10, flexWrap: "wrap" }}>
            <button type="button" style={estilos.btn} onClick={cerrarTodo}>Cancelar</button>
            <button type="button" style={estilos.btnPrimario} disabled={ocupado || !permisos.length}
              onClick={() => correr(() => actualizarFn({ integracion_id: it.id, permisos, contacto_email: contacto, motivo }), "Integración actualizada")}>
              Guardar
            </button>
          </div>
        </div>
      )}

      <div style={{ overflowX: "auto", marginTop: 14 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 560 }}>
          <thead>
            <tr style={{ color: C.ink3, textAlign: "left" }}>
              <th style={estilos.th}>Clave</th>
              <th style={estilos.th}>Estado</th>
              <th style={estilos.th}>Vence</th>
              <th style={estilos.th}>Último uso</th>
              <th style={estilos.th}></th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((c) => {
              const dias = diasPara(c.vence_at);
              const e = ESTADO_CLAVE[c.estado] || ESTADO_CLAVE.vencida;
              return (
                <tr key={c.id} style={{ borderTop: `0.5px solid ${C.line}` }}>
                  <td style={{ ...estilos.td, fontFamily: "'DM Mono', ui-monospace, Menlo, monospace" }}>{c.prefijo}…</td>
                  <td style={estilos.td}><Chip color={e.color}>{e.txt}</Chip></td>
                  <td style={{ ...estilos.td, color: c.estado === "activa" && dias <= 14 ? C.warn : C.ink2, fontVariantNumeric: "tabular-nums" }}>
                    {c.estado === "revocada" ? `Revocada el ${fecha(c.revocada_at)}` : fecha(c.vence_at)}
                    {c.estado === "activa" && <span style={{ color: C.ink3 }}> · en {dias} d</span>}
                  </td>
                  <td style={{ ...estilos.td, color: C.ink2, fontVariantNumeric: "tabular-nums" }}>{fechaHora(c.ultimo_uso_at)}</td>
                  <td style={{ ...estilos.td, textAlign: "right" }}>
                    {c.estado === "activa" && (
                      <button type="button" style={{ ...estilos.btnChico, color: C.bad }} disabled={ocupado}
                        onClick={() => { cerrarTodo(); setRevocando(c.id); }}>
                        Revocar
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {revocando && (
        <Confirmacion
          texto="Revocar corta el acceso de esa clave en el momento. No se puede deshacer: si hace falta, se genera otra."
          motivo={motivo} setMotivo={setMotivo} ocupado={ocupado} etiqueta="Revocar clave" peligro
          onCancelar={cerrarTodo}
          onConfirmar={() => correr(() => revocarFn({ clave_id: revocando, motivo }), "Clave revocada")}
        />
      )}
    </div>
  );
}

function Confirmacion({ texto, motivo, setMotivo, ocupado, etiqueta, peligro, onCancelar, onConfirmar }) {
  const valido = motivo.trim().length >= 5;
  return (
    <div style={{ ...estilos.recuadro, borderColor: peligro ? "#FECACA" : C.line, background: peligro ? "#FEF2F2" : C.bg }}>
      <p style={{ margin: "0 0 8px", fontSize: 13, color: C.ink2 }}>{texto}</p>
      <input id="confirmacion-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo (obligatorio)" style={{ ...estilos.input, width: "100%", boxSizing: "border-box" }} autoFocus />
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10, flexWrap: "wrap" }}>
        <button type="button" style={estilos.btn} onClick={onCancelar}>Cancelar</button>
        <button type="button" disabled={!valido || ocupado} onClick={onConfirmar}
          style={{ ...estilos.btnPrimario, background: peligro ? C.bad : C.ink, opacity: valido ? 1 : 0.4 }}>
          {etiqueta}
        </button>
      </div>
    </div>
  );
}

// ── Panel ───────────────────────────────────────────────────────────────────
export default function IntegracionesPanel({ tenants = [] }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState("");
  const [creando, setCreando] = useState(false);
  const [claveNueva, setClaveNueva] = useState(null);
  const [aviso, setAviso] = useState(null);

  const avisar = useCallback((texto, tipo) => {
    setAviso({ texto, tipo });
    setTimeout(() => setAviso(null), 4000);
  }, []);

  const cargar = useCallback(async () => {
    try {
      const { data } = await listarFn({});
      setDatos(data);
      setError("");
    } catch (err) {
      setError(err.message || "No se pudieron cargar las integraciones.");
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const nombreEmpresa = (id) => tenants.find((t) => t.id === id)?.name || id;

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: C.ink, margin: 0 }}>Integraciones (API)</h2>
        {!creando && datos && (
          <button type="button" style={estilos.btnPrimario} onClick={() => setCreando(true)}>+ Nueva integración</button>
        )}
      </div>
      <p style={{ fontSize: 13, color: C.ink3, margin: "0 0 20px" }}>
        Sistemas externos que leen datos de una empresa por la API, cada uno con su clave. Las claves vencen a los 90 días; al rotar, la anterior sigue 14 días más.
      </p>

      {error && <div style={{ ...estilos.tarjeta, color: C.bad }}>{error}</div>}
      {!datos && !error && <p style={{ color: C.ink3, fontSize: 14 }}>Cargando…</p>}

      {creando && datos && (
        <NuevaIntegracion
          tenants={tenants}
          permisosDisponibles={datos.permisos_disponibles}
          avisar={avisar}
          onCancelar={() => setCreando(false)}
          onCreada={(clave) => { setCreando(false); setClaveNueva(clave); cargar(); }}
        />
      )}

      {datos && datos.integraciones.length === 0 && !creando && (
        <div style={{ ...estilos.tarjeta, textAlign: "center", color: C.ink3, fontSize: 14 }}>
          Todavía no hay integraciones. Empezá por una empresa de prueba para que el integrador se conecte sin tocar datos reales.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {datos?.integraciones
          .slice()
          .sort((a, b) => nombreEmpresa(a.tenant_id).localeCompare(nombreEmpresa(b.tenant_id)) || a.nombre.localeCompare(b.nombre))
          .map((it) => (
            <Integracion
              key={it.id}
              it={it}
              empresa={nombreEmpresa(it.tenant_id)}
              permisosDisponibles={datos.permisos_disponibles}
              onClaveNueva={setClaveNueva}
              onCambio={cargar}
              avisar={avisar}
            />
          ))}
      </div>

      {claveNueva && <ClaveNueva datos={claveNueva} onCerrar={() => setClaveNueva(null)} />}

      {aviso && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 400, padding: "14px 20px", borderRadius: 10, fontSize: 14, fontWeight: 600,
          maxWidth: 360, boxShadow: "0 6px 24px rgba(0,0,0,0.18)",
          background: aviso.tipo === "error" ? "#fef2f2" : "#f0fdf4", color: aviso.tipo === "error" ? C.bad : C.ok,
        }}>
          {aviso.texto}
        </div>
      )}
    </div>
  );
}

const estilos = {
  tarjeta: { background: "white", borderRadius: 12, padding: 20, border: `0.5px solid ${C.line}`, boxShadow: "0 2px 12px rgba(0,0,0,0.05)", marginBottom: 14 },
  recuadro: { marginTop: 14, padding: 14, borderRadius: 8, border: `0.5px solid ${C.line}`, background: C.bg },
  campo: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
  etiqueta: { fontSize: 12, fontWeight: 600, color: C.ink3, textTransform: "uppercase", letterSpacing: "0.04em" },
  input: { padding: "9px 12px", borderRadius: 8, border: `1px solid ${C.line}`, fontSize: 14, color: C.ink, background: "white" },
  btn: { padding: "9px 16px", background: "transparent", color: C.ink3, border: `1px solid ${C.line}`, borderRadius: 8, cursor: "pointer", fontSize: 14 },
  btnPrimario: { padding: "9px 16px", background: C.ink, color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600 },
  btnChico: { padding: "6px 12px", background: C.bg, color: C.ink2, border: `1px solid ${C.line}`, borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 500 },
  th: { padding: "8px 8px", fontWeight: 600, fontSize: 12 },
  td: { padding: "10px 8px", verticalAlign: "middle" },
};
